import { useEffect, useMemo, useState } from "react";
import "./resultPage.css";

function ResultPage() {
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  const inputName = searchParams.get("name")?.trim() || "";
  const questionSetIdParam = searchParams.get("questionSetId");
  const scoreParam = searchParams.get("score");
  const recordIdParam = searchParams.get("recordId");
  const questionSetId = Number(questionSetIdParam);
  const score = Number(scoreParam);

  const RESULT_THUMBNAIL_IMAGE_MAP = {
    帽子船長: `/images/resultThumbnail/hat.webp`,
    長髮船長: `/images/resultThumbnail/longHair.webp`,
    鬍鬚船長: `/images/resultThumbnail/beard.webp`,
    小丑船長: `/images/resultThumbnail/clown.webp`,
    白領船長: `/images/resultThumbnail/whiteCollar.webp`,
  };

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [overallResult, setOverallResult] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const togglePage = () => setCurrentPage((p) => (p === 1 ? 2 : 1));

  const goMainPage = () => window.location.assign("/");

  const goEnterInfoPage = () => {
    const params = new URLSearchParams();
    if (recordIdParam) params.set("recordId", recordIdParam);

    window.location.assign(
      params.toString() ? `/enter-info?${params.toString()}` : "/enter-info",
    );
  };

  useEffect(() => {
    const controller = new AbortController();

    async function loadOverallResult() {
      if (!Number.isFinite(questionSetId) || !Number.isFinite(score)) {
        setErrorMessage("缺少測驗結果參數，請先完成測驗。");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage("");

      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "/api";
        const requestBase = apiBaseUrl.startsWith("http")
          ? apiBaseUrl
          : `${window.location.origin}${apiBaseUrl}`;

        const url = new URL(`${requestBase}/results/overall`);
        url.searchParams.set("questionSetId", String(questionSetId));
        url.searchParams.set("score", String(score));

        const response = await fetch(url.toString(), {
          signal: controller.signal,
        });

        const contentType = response.headers.get("content-type") || "";
        const responseBody = contentType.includes("application/json")
          ? await response.json()
          : { success: false, message: await response.text() };

        if (!response.ok || !responseBody.success) {
          throw new Error(responseBody.message || "Failed to load result");
        }

        setOverallResult(responseBody.data?.result || null);
      } catch (error) {
        if (error.name === "AbortError") return;
        setErrorMessage(error.message || "Failed to load result");
      } finally {
        setLoading(false);
      }
    }

    loadOverallResult();
    return () => controller.abort();
  }, [questionSetId, score]);

  const resultType = overallResult?.type || "";
  const resultSlogan = overallResult?.slogan || "";
  const resultSloganParts = resultSlogan
    .split("、")
    .map((part) => part.trim())
    .filter(Boolean);
  const resultSloganFirstLine = resultSloganParts.slice(0, 2).join("、");
  const resultSloganSecondLine = resultSloganParts.slice(2).join("、");
  const resultPrice = overallResult?.price || "";
  const resultTitle = overallResult?.description_1 || "";
  const resultDescription = overallResult?.description_2 || "";

  //   分享連結先放測試圖，等正式圖上傳後再更新連結
  //   帽子船長: `https://www.superdaddypb.com/share2FB/hat.html`,
  //   長髮船長: `https://www.superdaddypb.com/share2FB/longHair.html`,
  //   鬍鬚船長: `https://www.superdaddypb.com/share2FB/beard.html`,
  //   小丑船長: `https://www.superdaddypb.com/share2FB/clown.html`,
  //   白領船長: `https://www.superdaddypb.com/share2FB/whiteCollar.html`,

  const RESULT_SHARE_LINK_MAP = {
    // 帽子船長: `https://shorturl.at/4KSLG`,
    // 長髮船長: `https://shorturl.at/5dhWr`,
    // 鬍鬚船長: `https://shorturl.at/V3oMW`,
    // 小丑船長: `https://shorturl.at/elsuh`,
    // 白領船長: `https://shorturl.at/eFi3O`,
    帽子船長: `https://www.superdaddypb.com/share2FB/hat.html`,
    長髮船長: `https://www.superdaddypb.com/share2FB/longHair.html`,
    鬍鬚船長: `https://www.superdaddypb.com/share2FB/beard.html`,
    小丑船長: `https://www.superdaddypb.com/share2FB/clown.html`,
    白領船長: `https://www.superdaddypb.com/share2FB/whiteCollar.html`,
  };

  const shareUrl = RESULT_SHARE_LINK_MAP[resultType];

  const shareToFacebook = () => {
    const urlToShare = encodeURIComponent(shareUrl);
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlToShare}`;
    window.open(
      fbShareUrl,
      "_blank",
      "noopener,noreferrer,width=600,height=700",
    );
  };

  const shareSheet = async () => {
    const shareData = {
      title: "Super Daddy 測驗結果",
      text: `我是 ${inputName}，我的神隊友爸爸類型是：${resultType}！`,
      url: shareUrl,
    };

    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }

    // Fallback (desktop / unsupported browsers)
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareData.url);
      alert("已複製連結，可以貼到社群分享");
    } else {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareData.url,
        )}`,
        "_blank",
        "noopener,noreferrer,width=600,height=700",
      );
    }
  };

  const shareToInstagram = () => {
    window.open(
      "https://www.instagram.com/create/story",
      "_blank",
      "noopener,noreferrer,width=600,height=700",
    );
  };

  const handleSaveImage = async () => {
    const params = new URLSearchParams({
      questionSetId: questionSetIdParam,
      name: inputName,
      score: scoreParam,
      recordId: recordIdParam,
    });

    const url = `${process.env.REACT_APP_API_BASE_URL}/records/${recordIdParam}/screenshot?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      alert("產生圖片失敗");
      return;
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "super-daddy-result.png";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const resultHeroImage = RESULT_THUMBNAIL_IMAGE_MAP[resultType] || "";

  return (
    <main className="result-page">
      <div className="result-page-bg" />

      <section className="result-card">
        <img
          className="result-card__paper"
          src="/images/P7_Banner_01.webp"
          alt=""
        />

        <div className="result-card__content">
          {/* PAGE 1 */}
          <div
            className="result-card__page"
            style={{ display: currentPage === 1 ? "block" : "none" }}
          >
            <div className="result-header">
              <img
                className="result-header-banner"
                src="/images/P7_Banner_Text_01.webp"
                alt=""
              />
            </div>

            <picture className="result-hero">
              <img className="result-hero__img" src={resultHeroImage} alt="" />
            </picture>

            <section className="result-summary">
              <img
                className="result-summary__deco result-summary__deco--left"
                src="/images/P7_deco_01.webp"
                alt=""
              />

              <div className="result-summary__text">
                <span className="result-1">你的神隊友爸爸類型是：</span>
                <span className="result-2">
                  {`${resultType} `}
                  <span className="result-name-underline">{inputName}</span>
                </span>

                <span className="result-3">
                  {resultSloganFirstLine || resultSlogan}
                  {resultSloganSecondLine ? (
                    <>
                      <br />
                      {resultSloganSecondLine}
                    </>
                  ) : null}
                </span>
              </div>

              <img
                className="result-summary__deco result-summary__deco--right"
                src="/images/P7_deco_02.webp"
                alt=""
              />
            </section>
            <span className="result-value">懸賞金額{resultPrice}</span>
          </div>

          {/* PAGE 2 */}
          <div
            className="result-card__page"
            style={{ display: currentPage === 2 ? "block" : "none" }}
          >
            <div className="result-header-page-2 ">
              <span className="page-2-result-1">你的神隊友爸爸類型是：</span>
              <span className="page-2-result-2">
                {`${resultType} `}
                <span className="result-name-underline">{inputName}</span>
              </span>
            </div>

            <section className="result-detail ">
              <span className="result-detail-title">{resultTitle}</span>
              <p className="result-detail__text">
                {loading ? "結果載入中..." : null}
                {!loading && errorMessage ? errorMessage : null}
                {!loading && !errorMessage ? (
                  <>
                    詳細評語：
                    <br />
                    {resultDescription}
                  </>
                ) : null}
              </p>
            </section>

            <div className="result-actions">
              <div className="result-actions__list">
                <button
                  type="button"
                  onClick={goEnterInfoPage}
                  className="btn-1"
                >
                  <span className="btn-1-primary">了解你的神隊友爸爸潛力</span>
                  <span className="btn-1-secondary">
                    領取專屬你的爸爸升級分析
                  </span>
                </button>

                <button type="button" onClick={shareSheet} className="btn-2">
                  分享給朋友
                </button>

                <button
                  type="button"
                  onClick={shareToFacebook}
                  className="btn-2"
                >
                  分享到 Facebook (Android適用)
                </button>

                {/* <button
                  type="button"
                  onClick={shareToInstagram}
                  className="btn-2"
                >
                  分享到 Instagram
                </button> */}

                <button
                  type="button"
                  onClick={handleSaveImage}
                  className="btn-3"
                >
                  <svg
                    width="15px"
                    height="15px"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill="none"
                      stroke="#000"
                      strokeWidth="3"
                      d="M3,2 L3,21 L21,21 L21,3 L12,3 L12,14"
                    />
                    <path d="M12 16 L7 10 L17 10 Z" fill="#000000" />
                  </svg>
                  儲存結果圖
                </button>

                <button
                  type="button"
                  onClick={goMainPage}
                  className="btn-4 btn-back"
                >
                  返回測驗首頁
                </button>
              </div>
            </div>
          </div>

          <img className="spLogo" src="/images/P7_Logo_01.webp" alt="" />

          <button type="button" onClick={togglePage} className="result-toggle">
            {currentPage === 1 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon-arrow-down"
              >
                <path d="M5 12l7 7 7-7" />
                <path d="M5 12l7 7 7-7" transform="translate(0, -6)" />
                <path d="M5 12l7 7 7-7" transform="translate(0, -12)" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon-arrow-up"
              >
                <path d="M5 12l7-7 7 7" />
                <path d="M5 12l7-7 7 7" transform="translate(0, 6)" />
                <path d="M5 12l7-7 7 7" transform="translate(0, 12)" />
              </svg>
            )}
          </button>
        </div>
      </section>
    </main>
  );
}

export default ResultPage;
